import AppBar from '@material-ui/core/AppBar'
import Button from '@material-ui/core/Button'
import Tooltip from '@material-ui/core/Tooltip'
import ButtonGroup from '@material-ui/core/ButtonGroup'
import Box from '@material-ui/core/Box'
import IconButton from '@material-ui/core/IconButton'
import Toolbar from '@material-ui/core/Toolbar'
import Slide from '@material-ui/core/Slide'
import useScrollTrigger from '@material-ui/core/useScrollTrigger'
import { Theme, lighten, makeStyles } from '@material-ui/core/styles'
import MenuIcon from '@material-ui/icons/Menu'
import AddIcon from '@material-ui/icons/Add'
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos'
import Typography from '@material-ui/core/Typography'
import clsx from 'clsx'
import React, { FC, RefObject, useState } from 'react'
import { Link as RouterLink, matchPath, useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
    getCurrentCustomerEntity,
    getCurrentProvider,
    getCurrentUserInfo,
} from '../../../helpers/functions/manageSessionStorageData'
import MenuTopBar from '../MenuTopBar/MenuTopBar'
import palette, { ACOLAD_BLUE, ACOLAD_SOFT_GREY } from '../../../theme/palette'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { scrollActions } from '../../../redux/slices/scroll/scroll'
import { useTypedDispatch } from '../../../redux/store'
import {
    MEMSOURCE_PROJECTS_DETAILS_PATH,
    MEMSOURCE_PROJECTS_LIST_PATH,
    TEMPO_QUOTES_CREATE_PATH,
    XTRF_PROJECTS_DETAILS_PATH,
    XTRF_PROJECTS_LIST_PATH,
    XTRF_PROJECTS_NEW_PATH,
    XTRF_QUOTE_LIST_PATH,
    TEMPO_PROJECT_DETAILS_PATH,
    TEMPO_PROJECTS_LIST_PATH,
} from '../../../config/pathList'
import { Providers } from '../../../interfaces/Providers'
import { isAccessible, PERMISSION_TEMPO_CREATE_REQUEST } from '../../../helpers/functions/rolesAndPermissions'
import JobsIcon from '@material-ui/icons/ViewListOutlined'
import ProjectsIcon from '@material-ui/icons/WorkOutlineOutlined'

enum PathType {
    project = 'project',
    quote = 'quote',
}

interface Params {
    type: keyof typeof PathType
    id: string
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        boxShadow: '0px 1px 5px 0px rgba(196,192,196,1)',
        backgroundColor: 'white',
    },
    flexGrow: {
        flexGrow: 0.5,
    },
    logoutButton: {
        marginLeft: theme.spacing(1),
    },
    logoutIcon: {
        marginRight: theme.spacing(1),
    },
    logo: {
        width: '50px',
        padding: theme.spacing(1),
        marginLeft: theme.spacing(1),
    },
    navButton: {
        display: 'flex',
        color: theme.palette.common.black,
        margin: theme.spacing(1),
    },
    button: {
        marginRight: theme.spacing(2),
        backgroundColor: ACOLAD_BLUE,
        color: theme.palette.common.white,
        '&:hover': {
            backgroundColor: lighten(ACOLAD_BLUE, 0.2),
        },
    },
    profile: {
        fontWeight: 'bold',
        fontSize: '15px',
    },
    account: {
        fontSize: '12px',
    },
    contactButton: {
        backgroundColor: '#0b1c34',
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
}))

interface Props {
    onOpenNavBar?: () => void
    className?: string
    forwardedRef?: RefObject<HTMLElement>
}

const TopBar: FC<Props> = ({ onOpenNavBar, className, forwardedRef }) => {
    const classes = useStyles()
    const { t } = useTranslation()
    const dispatch = useTypedDispatch()
    const history = useHistory()
    const [scrollTarget, setScrollTarget] = useState<HTMLElement | undefined | null>(
        forwardedRef ? forwardedRef.current : undefined,
    )
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
    const { scrolling, stopScrolling } = scrollActions
    const locationIncludes = (loc) => matchPath(history.location.pathname, { path: loc, exact: true })

    let projectDetailsLocation = null
    if (locationIncludes(MEMSOURCE_PROJECTS_DETAILS_PATH)) {
        projectDetailsLocation = MEMSOURCE_PROJECTS_LIST_PATH
    } else if (locationIncludes(XTRF_PROJECTS_DETAILS_PATH)) {
        const params = locationIncludes(XTRF_PROJECTS_DETAILS_PATH).params as Params
        switch (params.type) {
            case PathType.project:
                projectDetailsLocation = XTRF_PROJECTS_LIST_PATH
                break
            case PathType.quote:
                projectDetailsLocation = XTRF_QUOTE_LIST_PATH
                break
            default:
                break
        }
    } else if (locationIncludes(TEMPO_PROJECT_DETAILS_PATH)) {
        projectDetailsLocation = TEMPO_PROJECTS_LIST_PATH
    }

    const provider = getCurrentProvider()
    const userInfo = getCurrentUserInfo()
    const isLoggedIn = Object.keys(userInfo).length !== 0
    const trigger = useScrollTrigger({ target: scrollTarget || undefined })

    React.useEffect(() => {
        if (forwardedRef) setScrollTarget(forwardedRef.current)
        if (trigger === true) dispatch(scrolling())
        return () => {
            dispatch(stopScrolling())
        }
    }, [trigger])

    const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const redirectNewProject = () => {
        if (provider === Providers.xtrf) {
            history.push(XTRF_PROJECTS_NEW_PATH)
        } else if (provider === Providers.tempo) {
            history.push(TEMPO_QUOTES_CREATE_PATH)
        }
    }

    const accountName = getCurrentCustomerEntity().description

    return (
        <Slide appear={false} direction="down" in={!trigger}>
            <AppBar className={clsx(classes.root, className)}>
                <Toolbar>
                    {isLoggedIn && (
                        <IconButton onClick={onOpenNavBar}>
                            <MenuIcon />
                        </IconButton>
                    )}
                    <RouterLink to="/">
                        <img alt="Logo" className={classes.logo} src="/images/logos/logo--white.svg" />
                    </RouterLink>
                    <Box color={palette.acolad.darkBlue} component="h2" ml={1}>
                        <Typography noWrap variant="h4">
                            Uber Translation Dashboard
                        </Typography>
                    </Box>
                    <div className={classes.flexGrow} />
                    <Box sx={{ mr: 2, ml: 2, width: '100%' }}>
                        <ButtonGroup
                            fullWidth
                            aria-label="text button group"
                            color="primary"
                            size="medium"
                            variant="text"
                        >
                            <Tooltip title="Jobs">
                                <Button href="/memsource/jobs/list">
                                    <JobsIcon />
                                </Button>
                            </Tooltip>
                            <Tooltip title="Projects">
                                <Button href="/memsource/projects/list">
                                    <ProjectsIcon />
                                </Button>
                            </Tooltip>
                            <Button href={`mailto:Uber_Prod_AM@acolad.com`} rel="noopener noreferrer">
                                Contact Production Team
                            </Button>
                            <Button href={`mailto:Uber_Tech@acolad.com`}>Contact Technical Support</Button>
                        </ButtonGroup>
                    </Box>
                    <div className={classes.flexGrow} />
                    {projectDetailsLocation && (
                        <RouterLink className={classes.navButton} to={projectDetailsLocation}>
                            <ArrowBackIosIcon fontSize="small" />
                            {projectDetailsLocation.includes(PathType.quote)
                                ? t('navigation.top_bar.quote_list')
                                : t('navigation.top_bar.project_list')}
                        </RouterLink>
                    )}
                    {(provider === Providers.xtrf ||
                        (provider === Providers.tempo && isAccessible(PERMISSION_TEMPO_CREATE_REQUEST))) && (
                        <Button className={classes.button} onClick={redirectNewProject} variant="contained">
                            <AddIcon fontSize="small" />
                            {t('navigation.top_bar.new_project')}
                        </Button>
                    )}
                    {isLoggedIn && (
                        <>
                            <Button
                                aria-controls="fade-menu"
                                aria-haspopup="true"
                                color="primary"
                                endIcon={<ExpandMoreIcon />}
                                onClick={handleMenuClick}
                            >
                                <div>
                                    <Typography className={classes.profile}>
                                        {userInfo ? userInfo.name : 'Profile'}
                                    </Typography>
                                    <Typography className={classes.account}>{accountName ?? ''}</Typography>
                                </div>
                            </Button>
                            <MenuTopBar anchorEl={anchorEl} setAnchorEl={setAnchorEl} />
                        </>
                    )}
                </Toolbar>
            </AppBar>
        </Slide>
    )
}

export default TopBar
